import { COL_PRODUCT_COMPONENTS } from '../config/collections'
import { pb } from '../pocketbase'
import type {
  ConsumableRecord,
  ProductComponentRecord,
  ScheduleSlotRecord,
} from '../types/records'

export type MaterialRequirementRow = {
  consumableId: string
  consumable?: ConsumableRecord | null
  requiredQty: number
  stockQty: number
  deficit: number
}

export function filterBomForDate(rows: ProductComponentRecord[], dateIso: string): ProductComponentRecord[] {
  const day = dateIso.slice(0, 10)
  return rows.filter((row) => {
    const from = row.validFrom?.slice(0, 10)
    const to = row.validTo?.slice(0, 10)
    if (from && day < from) return false
    if (to && day > to) return false
    return true
  })
}

export function bomQuantityForOutput(
  components: ProductComponentRecord[],
  outputQty: number,
): Map<string, { qty: number; consumable?: ConsumableRecord | null }> {
  const map = new Map<string, { qty: number; consumable?: ConsumableRecord | null }>()
  const out = Number(outputQty) || 0
  if (out <= 0) return map

  for (const row of components) {
    const perUnit = Number(row.quantity) || 0
    const total = perUnit * out
    const prev = map.get(row.consumable)
    if (prev) {
      prev.qty += total
    } else {
      map.set(row.consumable, { qty: total, consumable: row.expand?.consumable ?? null })
    }
  }
  return map
}

export async function loadBomByProductIds(
  productIds: string[],
): Promise<Map<string, ProductComponentRecord[]>> {
  const unique = [...new Set(productIds.filter(Boolean))]
  const map = new Map<string, ProductComponentRecord[]>()
  if (unique.length === 0) return map

  const filter = unique.map((id) => `product="${id}"`).join(' || ')
  const rows = await pb.collection(COL_PRODUCT_COMPONENTS).getFullList<ProductComponentRecord>({
    filter,
    expand: 'consumable',
    sort: '+sort',
  })

  for (const row of rows) {
    const list = map.get(row.product) ?? []
    list.push(row)
    map.set(row.product, list)
  }
  return map
}

export function aggregateMaterialRequirements(
  slots: ScheduleSlotRecord[],
  bomByProductId: Map<string, ProductComponentRecord[]>,
): MaterialRequirementRow[] {
  const totals = new Map<string, { qty: number; consumable?: ConsumableRecord | null }>()

  for (const slot of slots) {
    if (slot.status === 'cancelled') continue
    const productId = slot.expand?.requestLine?.product
    if (!productId) continue

    const bomAll = bomByProductId.get(productId) ?? []
    const bom = filterBomForDate(bomAll, slot.plannedStart)
    const part = bomQuantityForOutput(bom, slot.plannedQuantity)

    for (const [consumableId, { qty, consumable }] of part) {
      const prev = totals.get(consumableId)
      if (prev) prev.qty += qty
      else totals.set(consumableId, { qty, consumable })
    }
  }

  return [...totals.entries()]
    .map(([consumableId, { qty, consumable }]) => {
      const requiredQty = Math.round(qty * 1000) / 1000
      const stockQty = Number(consumable?.stockQty) || 0
      return {
        consumableId,
        consumable,
        requiredQty,
        stockQty,
        deficit: Math.max(0, Math.round((requiredQty - stockQty) * 1000) / 1000),
      }
    })
    .sort((a, b) =>
      (a.consumable?.name ?? a.consumableId).localeCompare(b.consumable?.name ?? b.consumableId, 'ru'),
    )
}

export function hasMaterialDeficit(rows: MaterialRequirementRow[]): boolean {
  return rows.some((r) => r.deficit > 0)
}
